/**
 * File Service for Web Chat
 * Provides secure file access from the working directory
 * with .gitignore support and security measures
 */
import fs from 'fs';
import path from 'path';
import ignore, { Ignore } from 'ignore';

// Always excluded patterns (security + performance)
const ALWAYS_EXCLUDED = [
    'node_modules',
    'vendor',
    '.git',
    '.env',
    '.env.*',
    '.env.local',
    '.env.*.local',
    'dist',
    'build',
    '.next',
    '.nuxt',
    '.output',
    '.cache',
    '*.lock',
    'package-lock.json',
    'pnpm-lock.yaml',
    'yarn.lock',
    'composer.lock',
    '.DS_Store',
    'Thumbs.db',
    '*.log',
    '*.tmp',
    '*.temp',
    '.idea',
    '.vscode',
    '*.min.js',
    '*.min.css',
    '*.map',
    'coverage',
    '.nyc_output',
    '__pycache__',
    '*.pyc',
    '.pytest_cache',
    '.mypy_cache',
];

// Allowed text file extensions
const TEXT_EXTENSIONS = new Set([
    // Web
    '.html', '.htm', '.css', '.scss', '.sass', '.less',
    '.js', '.jsx', '.ts', '.tsx', '.mjs', '.cjs',
    '.vue', '.svelte', '.astro',
    // Data
    '.json', '.yaml', '.yml', '.toml', '.xml', '.csv',
    // Docs
    '.md', '.mdx', '.txt', '.rst', '.adoc',
    // Config
    '.env.example', '.gitignore', '.dockerignore', '.npmrc', '.nvmrc',
    '.editorconfig', '.prettierrc', '.eslintrc', '.babelrc',
    // Server
    '.php', '.py', '.rb', '.go', '.java', '.kt', '.scala',
    '.rs', '.swift', '.c', '.cpp', '.h', '.hpp', '.cs',
    // Shell
    '.sh', '.bash', '.zsh', '.fish', '.ps1', '.bat', '.cmd',
    // Database
    '.sql', '.prisma', '.graphql', '.gql',
    // Other
    '.dockerfile', '.makefile', '.rake', '.gradle',
]);

// Files without extension that are text
const TEXT_FILES = new Set([
    'Makefile', 'Dockerfile', 'Vagrantfile', 'Gemfile', 'Rakefile',
    'Procfile', 'Brewfile', '.gitignore', '.dockerignore', '.npmrc',
    '.nvmrc', '.env.example', 'LICENSE', 'README', 'CHANGELOG',
    'CONTRIBUTING', 'AUTHORS', 'CODEOWNERS',
]);

export interface FileInfo {
    path: string;       // Relative path from working dir
    name: string;       // File name only
    size: number;       // File size in bytes
    type: 'file' | 'dir';
    extension?: string; // File extension (without dot)
}

export interface FileContent {
    path: string;
    content: string;
    size: number;
    language: string;   // Syntax highlighting hint
}

export interface FileServiceOptions {
    workingDir: string;
    maxFileSize?: number;        // Default: 1MB
    maxFilesPerRequest?: number; // Default: 5
    maxSearchResults?: number;   // Default: 20
}

/**
 * Get language identifier for syntax highlighting
 */
function getLanguage(filePath: string): string {
    const ext = path.extname(filePath).toLowerCase();
    const langMap: Record<string, string> = {
        '.ts': 'typescript', '.tsx': 'typescript',
        '.js': 'javascript', '.jsx': 'javascript',
        '.mjs': 'javascript', '.cjs': 'javascript',
        '.py': 'python',
        '.rb': 'ruby',
        '.php': 'php',
        '.java': 'java',
        '.kt': 'kotlin',
        '.go': 'go',
        '.rs': 'rust',
        '.swift': 'swift',
        '.c': 'c', '.h': 'c',
        '.cpp': 'cpp', '.hpp': 'cpp',
        '.cs': 'csharp',
        '.css': 'css', '.scss': 'scss', '.sass': 'sass', '.less': 'less',
        '.html': 'html', '.htm': 'html',
        '.vue': 'vue',
        '.svelte': 'svelte',
        '.json': 'json',
        '.yaml': 'yaml', '.yml': 'yaml',
        '.xml': 'xml',
        '.md': 'markdown', '.mdx': 'markdown',
        '.sql': 'sql',
        '.graphql': 'graphql', '.gql': 'graphql',
        '.sh': 'bash', '.bash': 'bash', '.zsh': 'bash',
        '.dockerfile': 'dockerfile',
        '.prisma': 'prisma',
    };
    return langMap[ext] || 'text';
}

/**
 * Check if file is a text file based on extension
 */
function isTextFile(filePath: string): boolean {
    const ext = path.extname(filePath).toLowerCase();
    const name = path.basename(filePath);

    if (TEXT_FILES.has(name)) return true;
    if (ext && TEXT_EXTENSIONS.has(ext)) return true;

    // Special cases - config files without standard extension
    if (name.endsWith('rc') || name.endsWith('config')) return true;

    return false;
}

export class FileService {
    private workingDir: string;
    private maxFileSize: number;
    private maxFilesPerRequest: number;
    private maxSearchResults: number;
    private ignoreFilter: Ignore;

    constructor(options: FileServiceOptions) {
        this.workingDir = path.resolve(options.workingDir);
        this.maxFileSize = options.maxFileSize ?? 1024 * 1024; // 1MB
        this.maxFilesPerRequest = options.maxFilesPerRequest ?? 5;
        this.maxSearchResults = options.maxSearchResults ?? 20;

        // Initialize ignore filter
        this.ignoreFilter = ignore();
        this.loadIgnorePatterns();
    }

    /**
     * Load ignore patterns from .gitignore and add always-excluded patterns
     */
    private loadIgnorePatterns(): void {
        // Add always-excluded patterns
        this.ignoreFilter.add(ALWAYS_EXCLUDED);

        // Try to load .gitignore
        const gitignorePath = path.join(this.workingDir, '.gitignore');
        try {
            if (fs.existsSync(gitignorePath)) {
                const content = fs.readFileSync(gitignorePath, 'utf-8');
                this.ignoreFilter.add(content);
            }
        } catch {
            // Ignore errors reading .gitignore
        }
    }

    /**
     * Check if a path should be ignored
     */
    private isIgnored(relativePath: string): boolean {
        // Normalize path separators
        const normalizedPath = relativePath.replace(/\\/g, '/');
        return this.ignoreFilter.ignores(normalizedPath);
    }

    /**
     * Validate path is within working directory (prevent path traversal)
     */
    private validatePath(relativePath: string): string {
        // Remove leading slashes
        const cleanPath = relativePath.replace(/^\/+/, '');

        // Resolve to absolute path
        const absolutePath = path.resolve(this.workingDir, cleanPath);

        // Security check: must be within working directory
        if (!absolutePath.startsWith(this.workingDir + path.sep) && absolutePath !== this.workingDir) {
            throw new Error('Path traversal detected');
        }

        return absolutePath;
    }

    /**
     * Get relative path from working directory
     */
    private getRelativePath(absolutePath: string): string {
        return path.relative(this.workingDir, absolutePath);
    }

    /**
     * Search files matching a query (for autocomplete)
     * Returns files where path contains the query string
     */
    async searchFiles(query: string, limit?: number): Promise<FileInfo[]> {
        const maxResults = Math.min(limit ?? this.maxSearchResults, 50);
        const results: FileInfo[] = [];
        const normalizedQuery = query.toLowerCase().replace(/\\/g, '/');

        // Recursive search function
        const searchDir = async (dirPath: string, depth: number = 0): Promise<void> => {
            // Limit depth to prevent infinite loops
            if (depth > 10 || results.length >= maxResults) return;

            try {
                const entries = await fs.promises.readdir(dirPath, { withFileTypes: true });

                for (const entry of entries) {
                    if (results.length >= maxResults) break;

                    const entryPath = path.join(dirPath, entry.name);
                    const relativePath = this.getRelativePath(entryPath);

                    // Skip ignored paths
                    if (this.isIgnored(relativePath)) continue;

                    // Check if path matches query
                    const normalizedRelPath = relativePath.toLowerCase().replace(/\\/g, '/');
                    if (normalizedRelPath.includes(normalizedQuery)) {
                        // Only include text files
                        if (entry.isFile() && isTextFile(entry.name)) {
                            try {
                                const stat = await fs.promises.stat(entryPath);
                                results.push({
                                    path: relativePath,
                                    name: entry.name,
                                    size: stat.size,
                                    type: 'file',
                                    extension: path.extname(entry.name).slice(1) || undefined,
                                });
                            } catch {
                                // Skip files we can't stat
                            }
                        }
                    }

                    // Recurse into directories
                    if (entry.isDirectory()) {
                        await searchDir(entryPath, depth + 1);
                    }
                }
            } catch {
                // Skip directories we can't read
            }
        };

        await searchDir(this.workingDir);

        // Sort by path length (shorter paths first) then alphabetically
        results.sort((a, b) => {
            if (a.path.length !== b.path.length) {
                return a.path.length - b.path.length;
            }
            return a.path.localeCompare(b.path);
        });

        return results;
    }

    /**
     * Read a single file
     */
    async readFile(relativePath: string): Promise<FileContent> {
        const absolutePath = this.validatePath(relativePath);
        const normalizedRelPath = this.getRelativePath(absolutePath);

        // Check if ignored
        if (this.isIgnored(normalizedRelPath)) {
            throw new Error('File is excluded by ignore rules');
        }

        // Check if it's a text file
        if (!isTextFile(absolutePath)) {
            throw new Error('Only text files can be read');
        }

        // Check file exists and get stats
        const stat = await fs.promises.stat(absolutePath);

        if (!stat.isFile()) {
            throw new Error('Path is not a file');
        }

        // Check file size
        if (stat.size > this.maxFileSize) {
            throw new Error(`File exceeds maximum size of ${Math.round(this.maxFileSize / 1024)}KB`);
        }

        // Read file content
        const content = await fs.promises.readFile(absolutePath, 'utf-8');

        return {
            path: normalizedRelPath,
            content,
            size: stat.size,
            language: getLanguage(absolutePath),
        };
    }

    /**
     * Read multiple files (batch)
     */
    async readFiles(relativePaths: string[]): Promise<Array<FileContent & { error?: string }>> {
        // Limit number of files
        const paths = relativePaths.slice(0, this.maxFilesPerRequest);

        const results = await Promise.all(
            paths.map(async (filePath) => {
                try {
                    const content = await this.readFile(filePath);
                    return content;
                } catch (error) {
                    return {
                        path: filePath,
                        content: '',
                        size: 0,
                        language: 'text',
                        error: error instanceof Error ? error.message : 'Unknown error',
                    };
                }
            })
        );

        return results;
    }

    /**
     * Get working directory info
     */
    getWorkingDir(): string {
        return this.workingDir;
    }

    /**
     * Get max files per request limit
     */
    getMaxFilesLimit(): number {
        return this.maxFilesPerRequest;
    }
}
